import { WarningAlt } from "@carbon/icons-react";
import { Tooltip } from "@carbon/react";
import { UseModalOutput } from "@flaminem/ui-components";
import React, { JSX } from "react";

import { ModalTransition, PassiveModal } from "src/components/Modal";
import AppDataTable, { AppTableHeader } from "src/components/Tables/AppDataTable.tsx";
import { Comparison } from "src/dynamic-view-renderer/Widgets/RelatedCases/relatedCases.typings.ts";
import { useT } from "src/internationalization";

type ModalContainerProps = {
  title: string;
  subTitle: string;
  modalProps: UseModalOutput;
  renderTrigger: () => JSX.Element;
  comparison: Comparison;
};

type MatchTableItem = {
  id: string;
  code: string;
  val1: string;
  val2: string;
  warning: string | JSX.Element;
};

const MatchElementModal: React.FC<ModalContainerProps> = ({
  comparison,
  modalProps,
  renderTrigger,
  subTitle,
  title,
}) => {
  const t = useT();

  const headers: AppTableHeader[] = [
    {
      header: t("cases.table.property.column.title"),
      key: "code",
    },
    {
      header: t("component.widget.related.cases.modal.matched.table.header.alert", {
        element: comparison.elements.current,
      }),
      key: "val1",
    },
    {
      header: t("component.widget.related.cases.modal.matched.table.header.compared", {
        element: comparison.elements.compared,
      }),
      key: "val2",
    },
    {
      header: t("component.widget.related.cases.modal.matched.table.header.warning"),
      key: "warning",
    },
  ];

  const rows: MatchTableItem[] = comparison.identifiers.map((identifier, index) => {
    // in crossMatch: identical values are suspect
    const isWarning = comparison.match === "Cross" ? identifier.match : !identifier.match;

    const toolTipLabel =
      comparison.match === "Cross"
        ? t("component.widget.related.cases.modal.matched.table.row.tooltip.cross", {
            name: identifier.name,
          })
        : t("component.widget.related.cases.modal.matched.table.row.tooltip", {
            name: identifier.name,
          });

    return {
      id: `${index}`,
      code: identifier.name,
      val1: identifier.values[0] ?? "",
      val2: identifier.values[1] ?? "",
      warning: isWarning ? (
        <Tooltip label={toolTipLabel} align="left">
          <WarningAlt className="text-supportError" />
        </Tooltip>
      ) : (
        ""
      ),
    };
  });

  return (
    <>
      <div className="w-full">
        <div
          role="button"
          aria-label="View match details"
          style={{ cursor: "pointer", width: "100%" }}
          onClick={modalProps.openModal}
        >
          {renderTrigger()}
        </div>
      </div>
      <ModalTransition>
        {modalProps.isModalOpen && (
          <PassiveModal onClose={modalProps.closeModal} subtitle={subTitle} title={title} size="lg">
            <AppDataTable<MatchTableItem>
              headers={headers}
              rows={rows}
              data={rows}
              error={null}
              loading={false}
            />
          </PassiveModal>
        )}
      </ModalTransition>
    </>
  );
};

export default MatchElementModal;
